import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

// Sidebar avatar modal lives outside the main Turbo frame (moved to #modals).
// Multipart submit via fetch + Turbo Stream (no auto-upload — user clicks Enregistrer).
export default class extends Controller {
    static targets = ["input", "submitButton"];

    connect() {
        this.onFileDropSelected = this.onFileDropSelected.bind(this);
        this.element.addEventListener("file-drop:selected", this.onFileDropSelected);

        this.modalElement = this.element.closest(".modal");
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        document.addEventListener("keydown", this.onDocumentKeydown, true);

        this.updateSubmitState();
    }

    disconnect() {
        this.element.removeEventListener("file-drop:selected", this.onFileDropSelected);
        document.removeEventListener("keydown", this.onDocumentKeydown, true);
    }

    fileSelected() {
        this.updateSubmitState();
    }

    onFileDropSelected() {
        this.updateSubmitState();
    }

    onDocumentKeydown(event) {
        if (event.key !== "Enter") return;

        const modal = this.modalElement;
        if (!modal?.classList.contains("show")) return;
        if (!modal.contains(this.element)) return;
        if (event.target.closest("textarea")) return;
        if (event.target.closest("[data-bs-dismiss='modal']")) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.submitFromKeyboard();
    }

    submitFromKeyboard() {
        if (this.submitting) return;
        if (!this.hasInputTarget || !this.inputTarget.files?.length) return;
        if (!this.hasSubmitButtonTarget || this.submitButtonTarget.disabled) return;

        this.uploadFile(this.inputTarget.files[0]);
    }

    updateSubmitState() {
        if (!this.hasSubmitButtonTarget) return;

        const hasFile = this.hasInputTarget && this.inputTarget.files?.length > 0;
        this.submitButtonTarget.disabled = !hasFile || this.submitting;
    }

    submit(event) {
        event.preventDefault();

        if (this.submitting) return;
        if (!this.hasInputTarget || !this.inputTarget.files?.length) return;

        this.uploadFile(this.inputTarget.files[0]);
    }

    uploadFile(file) {
        const form = this.element;
        const formData = new FormData(form);
        formData.set("user[avatar]", file, file.name);

        const avatar = formData.get("user[avatar]");
        if (!(avatar instanceof File) || avatar.size === 0) return;

        this.submitting = true;
        this.updateSubmitState();

        fetch(form.action, {
            method: (form.method || "post").toUpperCase(),
            body: formData,
            headers: {
                Accept: "text/vnd.turbo-stream.html",
                "X-CSRF-Token": this.csrfToken(),
            },
            credentials: "same-origin",
        })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.text();
            })
            .then((html) => {
                Turbo.renderStreamMessage(html);
                if (this.hasInputTarget) this.inputTarget.value = "";
            })
            .catch((error) => {
                console.error("[sidebar-avatar-upload]", error);
            })
            .finally(() => {
                this.submitting = false;
                this.updateSubmitState();
            });
    }

    csrfToken() {
        return document.querySelector('meta[name="csrf-token"]')?.content || "";
    }
}
